import React, { useEffect, useState } from "react";
import {
  PartialCastForm,
  PartialCastProps,
} from "../../../types/UploadFieldsType";
import TextInput from "../formComp/TextInputOld";
import SelectInput from "../formComp/SelectInputOld";
import ToothSelector from "../formComp/ToothSelectorOld";

const PartialCast: React.FC<PartialCastProps> = ({ onChange }) => {
  const [form, setForm] = useState<PartialCastForm>({
    frameworkMaterial: "",
    desiredArch: "",
    majorConnectorUpperDesignType: "",
    majorConnectorUpperCreateWindow: "",
    majorConnectorLower: "",
    minorConnector: "",
    directRetainerTeeth: [],

    directRetainerClasp: "",

    indirectRetainer: "",
    rest: "",
    additionalNotes: "",
  });

  const handleToothClick = (type: "directRetainerTeeth", tooth: number) => {
    const toothStr = tooth.toString();

    setForm((prev) => {
      const updated = prev[type].includes(toothStr)
        ? prev[type].filter((t) => t !== toothStr)
        : [...prev[type], toothStr];
      return { ...prev, [type]: updated };
    });
  };

  const handleChange = (field: keyof PartialCastForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    onChange(form);
  }, [form, onChange]);

  const checkboxOptions = [
    "I Bar",
    "C Clasp",
    "Embrasure Clasp",
    "Full arch",
    "Other (Please mention details in *Additional Notes* below)",
  ];
  const selectedPreferences = form.directRetainerClasp
    ? form.directRetainerClasp.split(",").map((s) => s.trim())
    : [];

  return (
    <div className="w-full text-sm md:text-lg text-gray-900">
      <h2 className="font-bold mb-4 border-b-2 border-[#2fa9bf]">
        RPD FRAMEWORK
      </h2>

      <div className="w-[100%] md:w-[90%]">
        {/* Framework material */}
        <SelectInput
          label="Framework material"
          name="frameworkMaterial"
          value={form.frameworkMaterial}
          onChange={(e) => handleChange("frameworkMaterial", e.target.value)}
          options={[
            { value: "Co-Cr", label: "Co-Cr" },
            { value: "Peek / Composite", label: "Peek / Composite" },
          ]}
        />

        {/* Desired arch */}
        <SelectInput
          label="Desired arch"
          name="desiredArch"
          value={form.desiredArch}
          onChange={(e) => handleChange("desiredArch", e.target.value)}
          options={[
            { value: "Upper", label: "Upper" },
            { value: "Lower", label: "Lower" },
            { value: "Upper and Lower", label: "Upper and Lower" },
          ]}
        />

        {/* [Upper only] Major Connector - Design Type */}
        <SelectInput
          optional
          label="[Upper only] Major Connector - Design Type"
          name="majorConnectorUpperDesignType"
          value={form.majorConnectorUpperDesignType}
          onChange={(e) =>
            handleChange("majorConnectorUpperDesignType", e.target.value)
          }
          options={[
            { value: "Complete Palate", label: "Complete Palate" },
            { value: "Palatal Strap", label: "Palatal Strap" },
            { value: "Horseshoe", label: "Horseshoe" },
            {
              value: "Other",
              label:
                "Other(please mention details in *Additional Notes* below)",
            },
          ]}
        />

        {/* [Upper only] Major Connector - Create window */}
        <SelectInput
          optional
          label="[Upper only] Major Connector - Create window"
          name="majorConnectorUpperCreateWindow"
          value={form.majorConnectorUpperCreateWindow}
          onChange={(e) =>
            handleChange("majorConnectorUpperCreateWindow", e.target.value)
          }
          options={[
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
          ]}
        />

        {/* [Lower only] Major Connector */}
        <SelectInput
          optional
          label="[Lower only] Major Connector"
          name="majorConnectorLower"
          value={form.majorConnectorLower}
          onChange={(e) => handleChange("majorConnectorLower", e.target.value)}
          options={[
            { value: "Lingual Bar", label: "Lingual Bar" },
            { value: "Lingual Plate", label: "Lingual Plate" },
            {
              value: "Other",
              label:
                "Other(please mention details in *Additional Notes* below)",
            },
          ]}
        />

        {/* Minor connector */}
        <TextInput
          optional
          label="Minor connector"
          name="minorConnector"
          value={form.minorConnector}
          onChange={(e) => handleChange("minorConnector", e.target.value)}
        />

        {/* Direct retainer - Teeth to be clasped */}
        <div className="mb-2 flex">
          <label className="block mb-1 w-[35%]">
            Direct retainer - Teeth to be clasped{" "}
            <span className="text-gray-400 text-sm"> Optional</span>
          </label>
          <div className="w-[65%]">
            <ToothSelector
              toothNumbers={form.directRetainerTeeth}
              onToothClick={(tooth) =>
                handleToothClick("directRetainerTeeth", tooth)
              }
            />
          </div>
        </div>

        {/* Direct retainer - Prefered clasp design */}
        <div className="my-4 flex">
          <label className="block w-[36%]">
            Direct retainer- Prefered clasp design{" "}
            <span className="text-gray-400 text-sm">Optional</span>
          </label>
          <div className="grid w-auto grid-cols-1 gap-x-4 gap-y-1 mt-1">
            {checkboxOptions.map((option) => {
              const isChecked = selectedPreferences.includes(option);

              return (
                <label key={option} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    value={option}
                    checked={isChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      let updatedPreferences: string[];

                      if (checked) {
                        updatedPreferences = [...selectedPreferences, option];
                      } else {
                        updatedPreferences = selectedPreferences.filter(
                          (item) => item !== option
                        );
                      }

                      setForm((prev) => ({
                        ...prev,
                        directRetainerClasp: updatedPreferences.join(", "),
                      }));
                    }}
                  />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        </div>
        {/* Indirect retainer */}
        <TextInput
          optional
          label="Indirect retainer"
          name="indirectRetainer"
          value={form.indirectRetainer}
          onChange={(e) => handleChange("indirectRetainer", e.target.value)}
        />

        {/* Rest */}
        <TextInput
          optional
          label="Rest"
          name="rest"
          value={form.rest}
          onChange={(e) => handleChange("rest", e.target.value)}
        />

        {/* Additional notes */}
        <TextInput
          optional
          label="Additional notes"
          name="additionalNotes"
          value={form.additionalNotes}
          onChange={(e) => handleChange("additionalNotes", e.target.value)}
        />
      </div>
    </div>
  );
};

export default PartialCast;
